import { useEffect, useMemo, useState } from "react";
import { Download, Eye, Loader2, Target, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * HR KPI Monthly Export.
 *
 * Separate from "HR Export (Monthly JSON)" above, which stays exactly as it is.
 * That one is a raw month-filtered dump of the database; this one emits the
 * aggregated per-unit KPI figures the HR KPI Center actually imports.
 *
 * Every figure comes from the same calculation behind the Reports screen, so the
 * Reports value, the /api/reports/summary value and the HR value cannot drift.
 */
export default function HrKpiExportPanel() {
  const [opt, setOpt] = useState(null);
  const [fy, setFy] = useState("");
  const [month, setMonth] = useState("");
  const [units, setUnits] = useState([]);          // NPD unit codes, e.g. ["U1","U3"]
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadOptions = async (forFy) => {
    try {
      const r = await api.get("/hr-kpi-export/options", { params: forFy ? { fy: forFy } : {} });
      setOpt(r.data);
      setFy((f) => f || r.data.fy);
      setUnits((u) => (u.length ? u : (r.data.units || []).map((x) => x.unit_code)));
      setMonth((m) => {
        if (m) return m;
        const done = (r.data.months || []).filter((x) => x.complete);
        return done.length ? done[done.length - 1].month : (r.data.months?.[0]?.month || "");
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load export options.");
    }
  };

  useEffect(() => { loadOptions(); }, []);
  useEffect(() => { if (fy && opt && fy !== opt.fy) loadOptions(fy); }, [fy]);  // eslint-disable-line

  const monthMeta = useMemo(
    () => (opt?.months || []).find((m) => m.month === month),
    [opt, month]
  );

  const toggleUnit = (code) =>
    setUnits((u) => (u.includes(code) ? u.filter((x) => x !== code) : [...u, code]));

  const params = () => ({ fy, month, units: units.join(",") });

  const doPreview = async () => {
    setBusy(true); setErr(""); setPreview(null);
    try {
      const r = await api.get("/hr-kpi-export/preview", { params: params() });
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Preview failed.");
    } finally { setBusy(false); }
  };

  const doDownload = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/hr-kpi-export/download", { params: params(), responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const name = /filename="?([^";]+)"?/.exec(cd)?.[1] || `npd-hr-kpi-${fy}-${month}.json`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Download failed.");
    } finally { setBusy(false); }
  };

  if (!opt) {
    return (
      <div className="mt-8 rounded-xl border bg-white p-5 text-sm text-slate-500">
        {err || "Loading HR KPI export…"}
      </div>
    );
  }

  const canRun = fy && month && units.length > 0 && monthMeta?.started;

  return (
    <div className="mt-8 rounded-xl border bg-white" data-testid="hr-kpi-export">
      <div className="border-b px-5 py-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <Target className="h-4 w-4 text-indigo-600" /> HR KPI Export (Monthly)
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Aggregated per-plant KPI figures for the HR KPI Center. Source{" "}
          <code className="rounded bg-slate-100 px-1">{opt.source}</code> ·{" "}
          {opt.metric_count} measurements per plant. This is separate from the monthly
          JSON dump above, which is unchanged.
        </p>
      </div>

      <div className="grid gap-5 p-5 md:grid-cols-3">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-slate-700">Financial year</span>
          <select value={fy} onChange={(e) => { setFy(e.target.value); setMonth(""); setPreview(null); }}
                  className="w-full rounded-md border px-2 py-1.5" data-testid="hr-kpi-fy">
            {opt.financial_years.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block font-medium text-slate-700">Month</span>
          <select value={month} onChange={(e) => { setMonth(e.target.value); setPreview(null); }}
                  className="w-full rounded-md border px-2 py-1.5" data-testid="hr-kpi-month">
            {opt.months.map((m) => (
              // a month that has not started cannot be exported — reporting 0 for it
              // would score the plant as perfect
              <option key={m.month} value={m.month} disabled={!m.started}>
                {m.month} {m.calendar_year}
                {!m.started ? " — not started" : m.in_progress ? " — in progress" : ""}
              </option>
            ))}
          </select>
        </label>

        <div className="text-sm">
          <span className="mb-1 block font-medium text-slate-700">
            Plants <span className="font-normal text-slate-400">(HR skips codes it does not claim)</span>
          </span>
          <div className="flex flex-wrap gap-3">
            {opt.units.map((u) => (
              <label key={u.unit_code} className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={units.includes(u.unit_code)}
                       onChange={() => { toggleUnit(u.unit_code); setPreview(null); }}
                       data-testid={`hr-kpi-unit-${u.unit_code}`} />
                <span>{u.unit_code} → <code className="rounded bg-slate-100 px-1">{u.hr_code}</code></span>
                <span className="text-slate-400">{u.unit_name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {monthMeta?.in_progress && (
        <div className="mx-5 mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          This month is still running. The file will carry
          <code className="mx-1 rounded bg-amber-100 px-1">month_complete: false</code>
          and figures counted only to today.
        </div>
      )}
      {err && (
        <div className="mx-5 mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{err}</div>
      )}

      <div className="flex flex-wrap gap-2 border-t px-5 py-4">
        <button onClick={doPreview} disabled={!canRun || busy} data-testid="hr-kpi-preview"
                className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
        </button>
        <button onClick={doDownload} disabled={!canRun || busy} data-testid="hr-kpi-download"
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-40">
          <Download className="h-4 w-4" /> Download JSON
        </button>
      </div>

      {preview && (
        <div className="border-t px-5 py-4">
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> ready
            </span>
            <span>source <code className="rounded bg-slate-100 px-1">{preview.source}</code></span>
            <span>format <code className="rounded bg-slate-100 px-1">absent (fallback route)</code></span>
            <span>{preview.fy} · {preview.month}</span>
            <span>{preview.period.from} → {preview.period.counted_to}</span>
            <span>month_complete <strong>{String(preview.month_complete)}</strong></span>
            <span>{preview.unit_count} plant(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Measurement</th>
                  {preview.units.map((u) => (
                    <th key={u.unit} className="px-3 py-2 text-right font-medium">
                      {u.unit} <span className="font-normal text-slate-400">{u.unit_name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(preview.units[0]?.data || {}).map((k) => (
                  <tr key={k} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-xs text-slate-600">{k}</td>
                    {preview.units.map((u) => (
                      <td key={u.unit} className="px-3 py-1.5 text-right tabular-nums">
                        {/* null is NOT 0 — it means not reported / not applicable */}
                        {u.data[k] === null || u.data[k] === undefined
                          ? <span className="text-slate-400">null</span>
                          : String(u.data[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
